import { Injectable, Injector } from '@angular/core';

import { LoggingService } from '../../logging/logic/logging.service';
import { ActionHandler, ActionHandlerType } from '../action-handler';
import { SyncService } from '../../sync/sync.service';
import { ActivityConfirmationsService } from '../../activity-confirmations/logic/activity-confirmations.service';
import { DraftPushNotifier, DraftPushParams } from './draft-push-notifier.service';

const I18N_PREFIX = 'SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.new_draft_available';
const I18N_PREFIX_ON_JOB =
  'SERVICE.PUSH-NOTIFICATION.LOCAL-NOTIFICATIONS.new_draft_on_job';

/**
 * APP4-948: a new draft version exists that concerns this device - either it was
 * released for this user, or it was parked on a job this device has.
 *
 * The push always triggers the sync; whether it is also SHOWN is the backend's call
 * via `slnt`. Silent covers the common case by far: the cloud autosave of a draft the
 * user is writing on their own other device. Announcing that would mean a notification
 * per autosave for something the user is doing right now.
 */
@Injectable({
  providedIn: 'root'
})
export class NewDraftAvailableActionHandler implements ActionHandler {
  public readonly name: ActionHandlerType = 'new_draft_available';

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private syncService: SyncService,
    private draftPushNotifier: DraftPushNotifier,
    private injector: Injector
  ) {}

  // ActionHandler
  public async executeAction(params?: DraftPushParams): Promise<any> {
    // Syncs the ActivityConfirmations, NOT the ExtFiles. A pure cloud draft creates
    // no ExtFile at all (only a DynForm with draftextfiles="true" does), and the AC
    // itself belongs to an ExtFileType with synctype `never` - an ExtFiles sync
    // therefore finds nothing and the pushed draft never becomes visible. The
    // standalone syncactivityconfirmations endpoint exists exactly for that gap.
    this.logger.log(
      this.constructor.name,
      'New draft available - triggering activity confirmations sync for',
      params
    );

    let synced = true;
    try {
      await this.syncService.syncActivityConfirmations(true);
    } catch (error) {
      // Still notify below - the user should learn about the draft even if it only
      // becomes visible after the next successful sync.
      synced = false;
      this.logger.error(
        this.constructor.name,
        'Sync of activity confirmations failed, notifying with push params only',
        error?.message
      );
    }

    // Notify only after the sync: the push carries ids, the AC carries the number
    // range, who saved the draft, when and on which device.
    const acId = this.draftPushNotifier.getAcId(params);
    const activityConfirmation = synced
      ? this.injector
          .get(ActivityConfirmationsService)
          .getActivityConfirmationById(acId)
      : undefined;

    // A draft parked on a job was not released for one person, it is waiting for
    // whoever picks it up - "shared with you" would be the wrong promise there.
    const i18nPrefix = this.draftPushNotifier.isServiceJobDraft(
      params,
      activityConfirmation,
    )
      ? I18N_PREFIX_ON_JOB
      : I18N_PREFIX;

    await this.draftPushNotifier.notify(i18nPrefix, params, activityConfirmation);
  }
}
